import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { token } from '@/lib/theme/tokens';

// eslint-disable-next-line react-refresh/only-export-components
export const surface = {
  card: 'rounded-2xl border border-border-subtle bg-surface shadow-none',
  cardCompact: 'rounded-xl border border-border-subtle bg-surface shadow-none',
  divider: 'border-border-subtle',
  label: 'text-2xs uppercase tracking-[0.15em] text-text-tertiary font-medium',
  title: 'text-sm font-semibold text-text-primary',
  body: 'text-sm text-text-secondary',
  value: 'font-mono tabular-nums text-text-primary',
  panel: 'rounded-xl bg-surface-sunken',
  mutedPanel: 'rounded-xl border border-border-subtle bg-surface-sunken',
};

export type SurfaceTone = 'neutral' | 'income' | 'expense' | 'warning';

// eslint-disable-next-line react-refresh/only-export-components
export const surfaceTone: Record<SurfaceTone, { text: string; bg: string; dot: string; chart: string }> = {
  neutral: {
    text: 'text-text-primary',
    bg: 'bg-surface-sunken',
    dot: 'bg-amount-neutral',
    chart: token('--amount-neutral', '#7A8A85'),
  },
  income: {
    text: 'text-success-text',
    bg: 'bg-success-surface',
    dot: 'bg-success',
    chart: token('--amount-positive', '#059669'),
  },
  expense: {
    text: 'text-error-text',
    bg: 'bg-error-surface',
    dot: 'bg-error',
    chart: token('--amount-negative', '#e11d48'),
  },
  warning: {
    text: 'text-warning-text',
    bg: 'bg-warning-surface',
    dot: 'bg-warning',
    chart: token('--warning', '#f59e0b'),
  },
};

export function SurfaceCard({
  className,
  children,
  as: Tag = 'section',
  ...props
}: {
  className?: string;
  children: ReactNode;
  as?: 'div' | 'section' | 'article' | 'li';
} & HTMLAttributes<HTMLElement>) {
  return <Tag className={cn(surface.card, className)} {...props}>{children}</Tag>;
}

export function SurfaceHeader({
  icon: Icon,
  title,
  description,
  action,
  tone = 'neutral',
  className,
}: {
  icon?: ComponentType<{ className?: string; weight?: 'duotone' | 'regular' | 'bold' }>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  tone?: SurfaceTone;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 items-center gap-2.5">
        {Icon && (
          <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-xl', surfaceTone[tone].bg, surfaceTone[tone].text)}>
            <Icon className="size-4" weight="duotone" />
          </span>
        )}
        <div className="min-w-0">
          <p className={cn(surface.label, 'truncate')}>{title}</p>
          {description && <div className="mt-0.5 text-xs text-text-secondary">{description}</div>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function MetricValue({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: SurfaceTone;
  className?: string;
}) {
  return (
    <p className={cn(surface.value, 'truncate text-[1rem] leading-none sm:text-[1.125rem]', surfaceTone[tone].text, className)}>
      {children}
    </p>
  );
}

export function EmptySurface({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(surface.cardCompact, 'flex min-h-32 items-center justify-center p-6 text-center text-sm text-text-secondary', className)}>
      {children}
    </div>
  );
}

export function SectionCard({
  eyebrow,
  title,
  hint,
  icon: Icon,
  tone = 'neutral',
  action,
  bare = false,
  framed = false,
  fill = false,
  className,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  hint?: ReactNode;
  icon?: ComponentType<{ className?: string; weight?: 'duotone' | 'regular' | 'bold' }>;
  tone?: SurfaceTone;
  action?: ReactNode;
  bare?: boolean;
  /** Header + content live INSIDE one card (the concept's chart/list panel shape), vs the default
   * header-above-a-card. Use for single-panel sections (chart/list); keep header-outside for
   * multi-card sections (KPI strips, goal grids). */
  framed?: boolean;
  /** Stretch the inner card to fill the full cell height (aligns bottoms when placed beside a taller sibling in a grid row). */
  fill?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const header = (
    <div className={cn('flex justify-between gap-3', framed ? 'mb-4 items-start' : 'mb-3 items-end')}>
      <div className="flex min-w-0 items-center gap-2.5">
        {Icon && (
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-xl',
              surfaceTone[tone].bg,
              surfaceTone[tone].text,
            )}
          >
            <Icon className="size-4" weight="duotone" />
          </span>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <p className="font-mono text-2xs font-semibold tracking-[0.14em] text-brand-700 uppercase">
              {eyebrow}
            </p>
          )}
          <h2 className="mt-0.5 text-2xl font-semibold tracking-tight text-text-primary">{title}</h2>
          {hint && <p className="mt-1.5 truncate text-sm text-text-tertiary">{hint}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );

  if (framed) {
    return (
      <section className={cn('@container min-w-0', fill && 'flex flex-col', className)}>
        <div className={cn('rounded-[var(--radius-4xl)] border border-border bg-surface p-4 shadow-[var(--shadow-1)] sm:p-6', fill && 'flex flex-1 flex-col')}>
          {header}
          {children}
        </div>
      </section>
    );
  }

  return (
    <section className={cn('@container min-w-0', fill && 'flex flex-col', className)}>
      {header}
      {bare ? (
        children
      ) : (
        <div className={cn('rounded-3xl border border-border-subtle bg-surface p-4 shadow-sm sm:p-5', fill && 'flex-1')}>
          {children}
        </div>
      )}
    </section>
  );
}

export function ChartTooltipSurface({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/95 px-3 py-2.5 text-xs text-white shadow-lg">
      {children}
    </div>
  );
}
